import styled, { css } from 'styled-components'
import { Button as BaseButton } from '@components/Button'
import { orange, danger, black, textMuted, darkGrey, mutedGrey, white, border2 } from '@/theme/colors'
import { shadows, radii, motion, media } from '@/theme/tokens'
import { fontSizes } from '@/theme/typography'
import { Input, Textarea, FieldLabel } from '@components/Field'

/*
 * The app's form kit: thin wrappers over the Field and Button primitives that
 * keep the names the modals already use (Itinerary, Trips, Profile, Onboarding).
 */

export const FormGroup = styled.div`
	margin-bottom: 20px;

	&:last-child {
		margin-bottom: 0;
	}
`

export const FormLabel = styled(FieldLabel)`
	display: block;
	margin-bottom: 8px;
`

export const FormInput = styled(Input)<{ $error?: boolean }>`
	${({ $error }) =>
		$error &&
		css`
			border-color: ${danger};

			&:focus {
				border-color: ${danger};
			}
		`}
`

export const FormTextarea = styled(Textarea)`
	min-height: 80px;
`

/** The kit's button is the app Button; pass `variant`. */
export const Button = BaseButton

export const ButtonGroup = styled.div<{ $align?: 'left' | 'center' | 'right' }>`
	display: flex;
	gap: 12px;
	justify-content: ${({ $align = 'right' }) => ({ left: 'flex-start', center: 'center', right: 'flex-end' })[$align]};
	flex-wrap: wrap;

	${media.phone} {
		gap: 8px;
	}
`

/** Dresses the MUI time picker as one of our fields. */
export const TimePickerWrapper = styled.div`
	width: 100%;

	.MuiTextField-root {
		width: 100%;
		background-color: ${white};
	}

	.MuiFormControl-root,
	.MuiPickersTextField-root,
	.MuiInputBase-root {
		background-color: ${white} !important;
	}

	.MuiOutlinedInput-root {
		border-radius: ${radii.inner};
		font-size: ${fontSizes.base};
		background-color: ${white} !important;
		transition:
			border-color ${motion.base},
			box-shadow ${motion.base};

		&:hover .MuiOutlinedInput-notchedOutline {
			border-color: ${darkGrey};
		}

		&.Mui-focused .MuiOutlinedInput-notchedOutline {
			border-color: ${orange};
			box-shadow: ${shadows.focus};
		}
	}

	.MuiOutlinedInput-notchedOutline {
		border-color: ${border2};
	}

	.MuiInputBase-input {
		padding: 12px 14px;
		font-size: ${fontSizes.base};
		color: ${black};
		background-color: ${white} !important;
	}

	.MuiInputBase-input::placeholder {
		color: ${mutedGrey};
		opacity: 1;
	}

	.MuiIconButton-root {
		color: ${textMuted};
		padding: 8px;

		&:hover {
			color: ${black};
			background-color: transparent;
		}
	}

	.MuiIconButton-root svg {
		width: 20px;
		height: 20px;
	}
`

export const FormFieldStack = styled.div`
	display: flex;
	flex-direction: column;
	gap: 24px;
`

export const LocationSecondaryText = styled.div`
	font-size: ${fontSizes.micro};
	color: ${textMuted};
	margin-top: 8px;
	padding-left: 4px;
`

export const LinksListWrapper = styled.div`
	margin-bottom: 8px;
`

export const LinkInputWrapper = styled.div`
	display: flex;
	gap: 8px;
	margin-bottom: 8px;
`

export const LinkInput = styled(Input)`
	flex: 1;
`

export const AddLinkButton = styled(BaseButton).attrs({ variant: 'secondary' as const })`
	width: 100%;
	margin-bottom: 16px;
`

export const RemoveLinkButton = styled(BaseButton).attrs({ variant: 'danger' as const, size: 'sm' as const })`
	padding: 0 10px;
`
